import { Accordion } from "PFComponents/accordion/accordion";
import MultiToggle, { Option } from "PFComponents/multi_toggle/multi_toggle";
import AutoSelect from "PFComponents/select/autoselect";
import Select from "PFComponents/select/select";
import { DateFormatKey, LocalesData } from "PFTypes";
import React, { useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { EditAccountForm, HoursFormat } from "../../edit_account.types";
import css from "../edit_account_page.module.scss";
import { transform } from "./language_section.utils";

type LanguageSectionProps = {
  localesData: LocalesData;
};

export const LanguageSection = ({ localesData }: LanguageSectionProps): JSX.Element => {
  const { t: locales } = useTranslation("locales");
  const { t } = useTranslation("saasAdmin", { keyPrefix: "accounts.account" });
  const { control, setValue } = useFormContext<EditAccountForm>();
  const [allowedLocales, locale] = useWatch({
    control,
    name: ["languageSettings.allowedLocales", "languageSettings.locale"]
  });

  useEffect(() => {
    if (allowedLocales.length === 0 || (locale !== "" && !allowedLocales.includes(locale))) {
      setValue("languageSettings.locale", "");
    }
  }, [allowedLocales, locale]);

  const defaultLanguagesDropdownOptions = useMemo(
    () =>
      allowedLocales.map((locale) => ({
        id: locale,
        displayElement: locales(locale as unknown as TemplateStringsArray),
        item: locale
      })),
    [allowedLocales]
  );

  const dateFormatOptions: Option<DateFormatKey>[] = useMemo(
    () =>
      localesData.availableDateFormats.map(({ key, displayFormat }) => ({ id: key, value: displayFormat })),
    []
  );

  const hoursFormat: Option<HoursFormat>[] = useMemo(
    () => [
      {
        id: "12h",
        value: "12"
      },
      {
        id: "24h",
        value: "24"
      }
    ],
    []
  );

  return (
    <div>
      <Accordion header={t("languageSettings.header")}>
        <div className={css.sectionContainer}>
          <Controller
            control={control}
            name={"languageSettings.allowedLocales"}
            rules={{
              required: {
                value: true,
                message: t("validationRequiredMessage")
              }
            }}
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <AutoSelect
                key={JSON.stringify(value)}
                letClear
                label={`${t("languageSettings.allowedLanguages")}*`}
                maxLength={5}
                multi
                values={transform.input(value, locales)}
                query={(rawTerm) => transform.query(rawTerm, localesData.availableLocales)}
                formatOption={(locale) => transform.formatOption(locale, locales)}
                handleChange={(locales) => onChange(transform.output(locales))}
                error={errors.languageSettings?.allowedLocales?.message}
              />
            )}
          />
          <Controller
            control={control}
            name={"languageSettings.locale"}
            rules={{
              required: {
                value: true,
                message: t("validationRequiredMessage")
              }
            }}
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <Select
                /* @ts-ignore */
                label={`${t("languageSettings.defaultLanguage")}*`}
                value={defaultLanguagesDropdownOptions.find(({ id }) => id === value)?.displayElement}
                controlledValue
                options={defaultLanguagesDropdownOptions}
                handleChange={onChange}
                selectedIndex={defaultLanguagesDropdownOptions.findIndex(({ id }) => id === value)}
                error={errors.languageSettings?.locale?.message}
              />
            )}
          />
          <Controller
            control={control}
            name={"languageSettings.dateFormatKey"}
            render={({ field: { onChange, value } }) => (
              <MultiToggle
                label={t("languageSettings.dateFormat")}
                options={dateFormatOptions}
                controlledValue={value}
                onChange={onChange}
                className={css.dateFormatContainer}
              />
            )}
          />
          <Controller
            control={control}
            name={"languageSettings.twelveHourClock"}
            render={({ field: { onChange, value } }) => (
              <MultiToggle
                label={t("languageSettings.hoursFormat")}
                options={hoursFormat}
                defaultSelected={value ? "12h" : "24h"}
                onChange={(selectedValue) => onChange(selectedValue === "12h")}
                className={css.hoursFormatContainer}
              />
            )}
          />
        </div>
      </Accordion>
    </div>
  );
};
